"use client";

import posthog from "posthog-js";
import React, { ReactNode, useContext, useEffect, useMemo, useState } from "react";
import { flushSync } from "react-dom";
import { usePathname } from "next/navigation";
import { UserContext, UserContextProps } from "@/contexts/user-context";
import { useLocations } from "@/hooks/use-locations";
import { formatStaffName } from "@/lib/format-data";
import { currentLocationTier, locationsSpanTimezones, setGlobalCurrentLocation, setGlobalTenantSpansTimezones, setGlobalTimezoneOverride, setGlobalTimezoneUser } from "@/lib/timezone";
import { LocationDetail } from "@/types/location";
import { ProviderSummary } from "@/types/provider";
import { fetchUserData } from "@/api/auth/login";
import { updateProviderPrimaryLocation } from "@/api/provider/admin/provider";
import { authService } from "@/services/auth-service";
import { setCurrentStorageFirebaseUid } from "@/stores/tenant-storage-helpers";
import { useTimezoneStore } from "@/stores/timezone-store";
export { UserContext, type UserContextProps };
type UserProviderProps = {
  children: ReactNode;
};
const UserProvider: React.FC<UserProviderProps> = ({
  children
}) => {
  const pathname = usePathname();
  const [currentUser, setCurrentUser] = React.useState<ProviderSummary | null | undefined>(undefined);
  const [currentLocationId, setCurrentLocationId] = useState<string | null>(null);
  const [locationSuggestion, setLocationSuggestion] = useState<{
    show: boolean;
    location: LocationDetail | null;
    distance: number;
  }>({
    show: false,
    location: null,
    distance: 0
  });

  // Get visible location IDs from current user
  const visibleLocationIds = currentUser?.visibleLocationIds || [];
  const refetchVisibleLocations = async () => {
    // Refetch the entire user data to get updated visible locations
    if (currentUser?.id) {
      await refetchUser();
    }
  };
  useEffect(() => {
    if (!currentUser) return;
    posthog.identify(currentUser.id);
    posthog.setPersonProperties({
      tenant: pathname.split("/")[1] || null,
      firstName: currentUser.firstName,
      lastName: currentUser.lastName,
      email: currentUser.email,
      name: formatStaffName(currentUser),
      isDecodaAdmin: currentUser.email?.endsWith("@decodahealth.com"),
      roles: currentUser.roles?.map(role => role.name) ?? [],
      roleTypes: currentUser.roles?.map(role_0 => role_0.userType) ?? []
    });
  }, [currentUser, pathname]);
  useEffect(() => {
    const setResolvedUser = (userData: ProviderSummary | null) => {
      flushSync(() => {
        setCurrentUser(userData);
        setCurrentLocationId(userData?.primaryLocationId ?? null);
      });
    };
    const unsubscribe = authService.addAuthStateListener(async firebaseUser => {
      let storageFirebaseUid: string | null = null;
      if (firebaseUser) {
        // Only fetch user when we have a tenant (from URL). On /login there's no tenant,
        // so skip - the login flow will fetch after tenant selection. This avoids
        // "Tenant determination failed" 400s when the auth listener fires on login.
        const tenantFromPath = typeof window !== "undefined" ? window.location.pathname.split("/")[1] : null;
        if (tenantFromPath && tenantFromPath !== "login") {
          let userData_0: ProviderSummary | null = null;
          try {
            userData_0 = await fetchUserData(firebaseUser.uid, tenantFromPath);
          } catch {
            // User not found in this tenant schema (e.g. 401) — treat as unauthenticated
          }
          if (authService.getCurrentUser()?.uid !== firebaseUser.uid) return;
          setResolvedUser(userData_0);
          storageFirebaseUid = userData_0 ? firebaseUser.uid : null;
        } else {
          setResolvedUser(null);
        }
      } else {
        setResolvedUser(null);
      }

      // Scoped stores reload synchronously, so commit the resolved user first
      // to ensure empty selections initialize from the matching user.
      setCurrentStorageFirebaseUid(storageFirebaseUid);
    });
    return () => unsubscribe();
  }, []);

  // Used internally for proximity check and auto-selecting single location
  const {
    locations,
    isLocationsLoading
  } = useLocations({
    currentUser
  });
  // Every location, not just the ones this user can see: whether the clinic
  // keeps one clock is a fact about the clinic.
  const {
    locations: allLocations
  } = useLocations({
    currentUser,
    seeAllLocations: true
  });
  // TenantProvider sits inside this one, so the default comes from the store
  // it writes.
  const tenantDefaultTimezone = useTimezoneStore(state => state.tenantDefaultTimezone);
  const tenantSpansTimezones = useMemo(() => locationsSpanTimezones(allLocations, tenantDefaultTimezone), [allLocations, tenantDefaultTimezone]);

  // Current selected location's timezone, mirrored into the timezone store so
  // non-hook `getTimezone()` callers resolve override > location > browser too.
  // Looked up among archived locations too, so a provider whose location was
  // archived keeps that location's clock.
  const {
    locations: allLocationsWithArchived
  } = useLocations({
    currentUser,
    seeAllLocations: true,
    includeArchived: true
  });
  const currentLocation = useMemo(() => currentLocationTier(currentLocationId, allLocationsWithArchived), [allLocationsWithArchived, currentLocationId]);

  // Written from an effect, never during render: only committed renders run
  // effects, so the store can never hold a tier from a render React discarded.
  // Scoping to the firebase uid first means a user switch clears the previous
  // user's tiers rather than leaving them readable.
  useEffect(() => {
    setGlobalTimezoneUser(currentUser?.firebaseUid ?? null);
    setGlobalTimezoneOverride(currentUser?.timezoneOverride ?? null);
    setGlobalCurrentLocation(currentLocation.timezone, currentLocation.selected);
    setGlobalTenantSpansTimezones(tenantSpansTimezones);
  }, [currentUser?.firebaseUid, currentUser?.timezoneOverride, currentLocation.timezone, currentLocation.selected, tenantSpansTimezones]);
  // Reset on unmount (logout / provider teardown) so a stale zone can't leak
  // into the next session.
  useEffect(() => () => {
    setGlobalTimezoneUser(null);
    setGlobalTenantSpansTimezones(false);
  }, []);
  useEffect(() => {
    if (locations.length === 1) {
      setCurrentLocationId(locations[0].id);
    }
  }, [locations]);

  // Proximity check
  useEffect(() => {
    // Only run this effect when we have a user, locations, and a selected location
    if (currentUser && locations.length > 1 && currentLocationId && !isLocationsLoading) {
      // Slight delay to ensure everything is loaded
      const timer = setTimeout(() => {
        checkUserProximityToLocations();
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [currentUser, locations, currentLocationId, isLocationsLoading]);
  const refetchUser = async (tenant?: string) => {
    const uid = authService.getCurrentUser()?.uid;
    if (!uid) {
      setCurrentUser(null);
      return;
    }
    const userData_1 = await fetchUserData(uid, tenant);
    if (authService.getCurrentUser()?.uid !== uid) return;
    flushSync(() => {
      setCurrentUser(userData_1);
      setCurrentLocationId(userData_1?.primaryLocationId ?? null);
    });
    if (userData_1) setCurrentStorageFirebaseUid(uid);
  };
  const updatePrimaryLocation = async (locationId: string | null): Promise<boolean> => {
    if (!currentUser?.id) return false;
    const success = await updateProviderPrimaryLocation(currentUser.id, locationId);
    if (success) {
      setCurrentLocationId(locationId);
      // Optimistically update currentUser so consumers reading
      // currentUser.primaryLocationId don't briefly see the stale value
      // during the follow-up refetchUser round-trip.
      setCurrentUser(prev => prev ? {
        ...prev,
        primaryLocationId: locationId ?? undefined
      } : prev);
      await refetchUser();
      return true;
    }
    return false;
  };

  // Calculate distance between two points using Haversine formula (in kilometers)
  const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
    const R = 6371; // Radius of the earth in km
    const dLat = (lat2 - lat1) * (Math.PI / 180);
    const dLon = (lon2 - lon1) * (Math.PI / 180);
    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) + Math.cos(lat1 * (Math.PI / 180)) * Math.cos(lat2 * (Math.PI / 180)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  // Check user's location and suggest nearest location if different from current
  const checkUserProximityToLocations = async () => {
    // Only check if user has multiple locations and a current location is selected
    if (!currentUser || !locations || locations.length <= 1 || !currentLocationId) {
      return;
    }

    // Get current location
    const currentLocation_0 = locations.find(loc => loc.id === currentLocationId);

    // Don't suggest location changes if current location is virtual
    if (currentLocation_0?.isVirtual) {
      return;
    }

    // Filter locations that have coordinates
    const locationsWithCoordinates = locations.filter(loc_0 => loc_0.latitude && loc_0.longitude);
    if (locationsWithCoordinates.length <= 1) {
      return;
    }
    try {
      // Request user's geolocation
      const position = await new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 5000,
          maximumAge: 0
        });
      });
      const userLat = position.coords.latitude;
      const userLng = position.coords.longitude;

      // Find closest location
      let closestLocation = null;
      let shortestDistance = Infinity;
      for (const location of locationsWithCoordinates) {
        if (location.latitude && location.longitude) {
          const distance = calculateDistance(userLat, userLng, location.latitude, location.longitude);
          if (distance < shortestDistance) {
            shortestDistance = distance;
            closestLocation = location;
          }
        }
      }

      // If closest location is different from current and within 100km, suggest changing
      if (closestLocation && closestLocation.id !== currentLocationId && shortestDistance < 100) {
        // Instead of window.confirm, set state for Alert component
        setLocationSuggestion({
          show: true,
          location: closestLocation,
          distance: shortestDistance
        });
      }
    } catch {
      console.log("Geolocation not available or permission denied");
    }
  };
  const dismissLocationSuggestion = () => {
    setLocationSuggestion({
      show: false,
      location: null,
      distance: 0
    });
  };
  const acceptLocationSuggestion = async () => {
    if (locationSuggestion.location) {
      await updatePrimaryLocation(locationSuggestion.location.id);
      dismissLocationSuggestion();
    }
  };
  return <UserContext.Provider value={{
    currentUser,
    setCurrentUser,
    refetchUser,
    currentLocationId,
    updatePrimaryLocation,
    visibleLocationIds,
    refetchVisibleLocations,
    locationSuggestion,
    dismissLocationSuggestion,
    acceptLocationSuggestion
  }}>
      {children}
    </UserContext.Provider>;
};
export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error("useUser must be used within an UserProvider");
  }
  return context;
};
export default UserProvider;